import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import ReactGA from "react-ga4";

const GA_ID = process.env.REACT_APP_GA_ID;

export const initGA = () => {
  if (!GA_ID) return;
  ReactGA.initialize(GA_ID);
};

export const trackPageView = (path) => {
  if (!GA_ID) return;
  ReactGA.send({ hitType: "pageview", page: path });
};

export const trackEvent = (name, params = {}) => {
  if (!GA_ID) return;
  ReactGA.event(name, params);
};

const toGaItem = (p) => ({
  item_id: String(p.sku || p.id),
  item_name: p.name || p.title || String(p.id),
  price: Number(p.price) || 0,
  quantity: Number(p.quantity) || 1,
});

export const cartValue = (items) =>
  items.reduce(
    (sum, p) => sum + (Number(p.price) || 0) * (Number(p.quantity) || 1) + (Number(p.setupFee) || 0),
    0
  );

export const trackBeginCheckout = (items) =>
  trackEvent("begin_checkout", {
    currency: "EUR",
    value: cartValue(items),
    items: items.map(toGaItem),
  });

export const trackPurchase = (transactionId, items) =>
  trackEvent("purchase", {
    transaction_id: String(transactionId || ""),
    currency: "EUR",
    value: cartValue(items),
    items: items.map(toGaItem),
  });

// Redux middleware: one place for cart/wishlist events instead of every call site.
export const analyticsMiddleware = (store) => (next) => (action) => {
  try {
    switch (action.type) {
      case "cart/addToCart":
        trackEvent("add_to_cart", {
          currency: "EUR",
          value: cartValue([action.payload]),
          items: [toGaItem(action.payload)],
        });
        break;
      case "cart/deleteFromCart": {
        const item = store.getState().cart.cartItems.find((i) => i.cartItemId === action.payload);
        if (item) {
          trackEvent("remove_from_cart", {
            currency: "EUR",
            value: cartValue([item]),
            items: [toGaItem(item)],
          });
        }
        break;
      }
      case "wishlist/addToWishlist":
        trackEvent("add_to_wishlist", { currency: "EUR", items: [toGaItem(action.payload)] });
        break;
      default:
    }
  } catch (e) {
    // analytics must never break the app
  }
  return next(action);
};

// Must be rendered inside <Router>: sends a page_view on every route change.
export const RouteTracker = () => {
  const { pathname, search } = useLocation();
  useEffect(() => {
    trackPageView(pathname + search);
  }, [pathname, search]);
  return null;
};
